import React, { memo, useContext, useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'

import {
  checkSentimentStatus,
  getLastItemPath,
  getLastSentimentValue,
  isVideoOrImageType,
  isXpertOrVirtualType,
  pathQuestionIdToOld,
  questionByPath,
  questionOldIdToId,
  responseTagsByPath
} from '@aytm/app/stats/page/comments/lib/utils'
import { updateQACounter, changeResponseCommentStatus } from '@aytm/app/stats/page/comments/actions'
import { DEFAULT_SELECTED_QUESTION } from '@aytm/app/stats/page/comments/lib/constants'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import OEItem from '@aytm/app/stats/page/comments/comments/OEItem'
import Traits from '@aytm/app/stats/page/comments/comments/components/Response/Traits'
import Kudos from '@aytm/app/stats/page/comments/comments/components/Response/Kudos'
import Reject from '@aytm/app/stats/page/comments/comments/components/Response/Reject'
import Approve from '@aytm/app/stats/page/comments/comments/components/Response/Approve'
import Translation from '@aytm/app/stats/page/comments/comments/components/Response/Translation'
import ResponseDropdown from '@aytm/app/stats/page/comments/comments/components/Response/ResponseDropdown'

import { OEContext } from '@aytm/app/stats/page/comments/index'

import style from '@aytm/app/stats/page/comments/comments/styles/Response.module.scss'

const Response = memo(
  React.forwardRef(
    (
      {
        dataId,
        survey,
        reportVirtual,
        reportSchema,
        response,
        toolBoxOptions,
        showTraitsRef,
        responseOrderRef,
        isOnlyVirtualOrXpert,
        popupNotificationsDisabled,
        isShowHiddenFilters,
        onUpdateSchema,
        onUpdateParams,
        onCloseNotification
      },
      ref
    ) => {
      const { searchState, translation, commentStatusesState, dispatch, dispatchCommentStatuses } =
        useContext(OEContext)

      const {
        CSRF,
        qaTotal,
        qaCounter,
        request,
        sentimentOptions,
        isShowAllTraits,
        isShowSentiments,
        isTagsManagementDisabled,
        isAdmin,
        isSurveyGuest,
        isRejectAvailable,
        isSurveyOwner,
        isQAMode,
        isVideoResponsesMode
      } = searchState

      const { uid: responseId } = response.response

      const { selectedQuestion, pageSize } = request

      const rejectStatus = commentStatusesState.reject[responseId]

      const isOnePerPage = isQAMode && 1 == pageSize
      const isRejected = !(isQAMode && isOnePerPage) && rejectStatus && 'true' == rejectStatus

      const hasTraits = Boolean(response.trait && Object.keys(response.trait).length > 4)
      const hasQuestionOrder = Boolean(response.session && response.session.order)

      const panelValue = (response.trait && response.trait.panel) || (response.variables && response.variables.panel)

      const responseDate = moment(
        new Date(response.sessionFinishAt ? response.sessionFinishAt : response.completedAt)
      ).utcOffset(window.surveyTimeZone.offset)

      const [voiceBusy, setVoiceBusy] = useState(false)

      const [rejectReason, setRejectReason] = useState()
      const [kudosConfirm, setKudosConfirm] = useState()

      const [responseTranslation, setResponseTranslation] = useState(null)

      const [isResponseOrder, setIsResponseOrder] = useState(searchState.isResponseOrder || false)
      const [isShowToolsDropdown, setIsShowToolsDropdown] = useState(false)
      const [isShowTraits, setIsShowTraits] = useState(isShowAllTraits || false)
      const [isShowOtherResponses, setIsShowOtherResponses] = useState(false)

      const questionsKeys = useMemo(() => {
        const answers =
          hasQuestionOrder && isResponseOrder
            ? response.session.order.filter(keyName => {
                const newKeyName = questionOldIdToId(keyName, survey)
                return (reportSchema.length
                  ? reportSchema.some(schemaKey => newKeyName == questionOldIdToId(schemaKey, survey))
                  : true) &&
                  selectedQuestion !== DEFAULT_SELECTED_QUESTION &&
                  !isShowOtherResponses
                  ? 0 == selectedQuestion.indexOf(newKeyName)
                  : true
              })
            : (reportSchema.length ? reportSchema : Object.keys(response.answer)).reduce((acc, cur) => {
                const newKeyName = questionOldIdToId(cur, survey)
                if (
                  selectedQuestion == DEFAULT_SELECTED_QUESTION ||
                  (selectedQuestion !== DEFAULT_SELECTED_QUESTION &&
                    !isShowOtherResponses &&
                    0 == selectedQuestion.indexOf(newKeyName))
                )
                  response.answer[cur] && acc.push(cur)
                return acc
              }, [])
        return answers.map(keyName => {
          return {
            keyName,
            path: questionOldIdToId(keyName, survey),
            question: questionByPath(keyName, survey, reportVirtual)
          }
        })
      }, [
        response,
        isShowOtherResponses,
        isResponseOrder,
        hasQuestionOrder,
        reportSchema,
        reportVirtual,
        selectedQuestion,
        survey
      ])

      const lastItemPath = getLastItemPath(questionsKeys, selectedQuestion, response)
      const lastItem = lastItemPath ? questionByPath(lastItemPath, survey, reportVirtual) : null
      const lastSentimentValue = lastItemPath ? getLastSentimentValue(lastItemPath, response) : 'None'
      const sentimentStatus =
        !lastItem || (lastItem && (isXpertOrVirtualType(lastItem) || isVideoOrImageType(lastItem)))
          ? false
          : checkSentimentStatus(lastItemPath, sentimentOptions)

      const onChangeResponseStatus = async (responseId, action, reason = null) => {
        const params = {
          type: action,
          responseId,
          scope: action,
          commentStatusesState,
          dispatchCommentStatuses,
          CSRF,
          onSuccess: () => {
            if (isQAMode) {
              if (isOnePerPage) {
                dispatch({
                  type: 'SET_PARAMS',
                  request: {
                    value: {
                      qaProcessedResponseId: responseId,
                      qaAction: action
                    }
                  }
                })
              }
              dispatch({
                type: 'ADD_PROCESSED_QA',
                request: {
                  responseId
                }
              })
              // delayed counter update to allow animation ends for isOnePerPage
              setTimeout(
                () => {
                  updateQACounter({ dispatch, qaTotal, qaCounter })
                },
                isOnePerPage ? 600 : 0
              )
            }
          }
        }
        if (reason) {
          params.reason = reason
        }
        await changeResponseCommentStatus(params)
      }

      const onToggleOtherResponses = () => setIsShowOtherResponses(!isShowOtherResponses)

      const onToggleToolsDropdown = () => setIsShowToolsDropdown(!isShowToolsDropdown)

      const onToggleResponseOrder = isResponseOrder => setIsResponseOrder(isResponseOrder)

      const onToggleTraits = () => setIsShowTraits(!isShowTraits)

      useEffect(() => {
        showTraitsRef.current[responseId] = setIsShowTraits
        if (hasQuestionOrder) {
          responseOrderRef.current[responseId] = setIsResponseOrder
        }
        return () => {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          delete showTraitsRef.current[responseId]
          if (hasQuestionOrder) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            delete responseOrderRef.current[responseId]
          }
        }
      }, [response, isShowAllTraits, hasQuestionOrder, responseId, responseOrderRef, showTraitsRef])

      return 0 < questionsKeys.length ? (
        <div
          ref={ref}
          className={classNames(style['response-container'], {
            [style['noborder']]: isTagsManagementDisabled,
            [style['is-rejected']]: isRejected
          })}
          data-id={dataId}
        >
          {isRejected && response.rejection ? (
            <div className={style['response-rejected-info']}>
              by {response.rejection.rejected_by},&nbsp;
              {moment(new Date(response.rejection.rejected_at)).format('MM/DD/YYYY [at] hh:mm A')}
            </div>
          ) : null}
          <div className={style['response-header']}>
            <div className={style['response-header-title']}>
              <i
                className={classNames('icon-aytmfd-singleuser', {
                  'afd-disabled': !hasTraits
                })}
                onClick={hasTraits ? onToggleTraits : null}
              />
              <Popup
                trigger={panelValue && isAdmin && isShowHiddenFilters ? 'hover' : 'none'}
                direction="top"
                popupOffset={14}
                show={panelValue && isAdmin && isShowHiddenFilters ? null : false}
                hideOtherPopupsOnShow
                content={() => <div>{panelValue ? `Panel: ${panelValue}` : null}</div>}
                contentTheme={'compact-black'}
              >
                <b>Response ID:</b> {responseId}
              </Popup>
              <Popup
                trigger="hover"
                direction="top"
                popupOffset={14}
                hideOtherPopupsOnShow
                content={() => (
                  <div>{responseDate.format(`hh:mma [${window.surveyTimeZone.name}] dddd MMMM DD, YYYY`)}</div>
                )}
                contentTheme={'compact-black'}
              >
                <div className={style['response-header-title-time']}>{responseDate.format('ddd hh:mma')}</div>
              </Popup>
            </div>
            <div className={style['response-header-right']}>
              {!rejectReason && !isRejected && !kudosConfirm && !isOnlyVirtualOrXpert ? (
                <Translation
                  response={response}
                  responseTranslation={responseTranslation}
                  setResponseTranslation={setResponseTranslation}
                />
              ) : null}
              {isQAMode && isOnePerPage && !rejectReason && !isRejected && !kudosConfirm ? (
                <Approve response={response} responseId={responseId} onChangeResponseStatus={onChangeResponseStatus} />
              ) : null}
              {!kudosConfirm && isRejectAvailable && !isSurveyGuest && (isAdmin || isSurveyOwner) ? (
                <Reject
                  response={response}
                  responseId={responseId}
                  rejectReason={rejectReason}
                  isRejected={isRejected}
                  popupNotificationsDisabled={popupNotificationsDisabled}
                  setRejectReason={setRejectReason}
                  onCloseNotification={onCloseNotification}
                  onChangeResponseStatus={onChangeResponseStatus}
                />
              ) : null}
              {!rejectReason && !isRejected && !isSurveyGuest && (isAdmin || isSurveyOwner) ? (
                <Kudos
                  response={response}
                  responseId={responseId}
                  kudosConfirm={kudosConfirm}
                  popupNotificationsDisabled={popupNotificationsDisabled}
                  setKudosConfirm={setKudosConfirm}
                  onCloseNotification={onCloseNotification}
                />
              ) : null}
              {!isRejected &&
              !rejectReason &&
              !kudosConfirm &&
              (hasTraits || (!hasTraits && isVideoResponsesMode) || hasQuestionOrder || isShowOtherResponses) ? (
                <div className={style['response-header-menu']}>
                  <i
                    className={classNames('icon-aytmfd-hotdog', {
                      'afd-disabled': !hasQuestionOrder && !hasTraits && !isVideoResponsesMode
                    })}
                    title={`${isShowToolsDropdown ? 'Hide' : 'Show'} all tools`}
                    onClick={!hasQuestionOrder && !hasTraits && !isVideoResponsesMode ? null : onToggleToolsDropdown}
                  />
                  <ResponseDropdown
                    responseId={responseId}
                    opened={isShowToolsDropdown}
                    hasQuestionOrder={hasQuestionOrder}
                    isResponseOrder={isResponseOrder}
                    isShowOtherResponses={isShowOtherResponses}
                    isShowTraits={isShowTraits}
                    onUpdateParams={onUpdateParams}
                    onToggleOtherResponses={onToggleOtherResponses}
                    onToggleResponseOrder={onToggleResponseOrder}
                    onToggleTraits={hasTraits ? onToggleTraits : null}
                    onClickOutside={onToggleToolsDropdown}
                  />
                </div>
              ) : null}
            </div>
          </div>
          {isShowTraits && hasTraits ? (
            <div className={style['response-traits']}>
              <Traits response={response} survey={survey} />
            </div>
          ) : null}
          <div className={style['response-body']}>
            {questionsKeys.map(({ keyName, path, question }) => {
              if (!question) return null

              const answer =
                '*' != (responseTranslation || translation) &&
                Object.hasOwn(response, 'extra') &&
                Object.hasOwn(response.extra, 'answer') &&
                Object.hasOwn(response.extra.answer, responseTranslation || translation) &&
                Object.hasOwn(response.extra.answer[responseTranslation || translation], keyName)
                  ? response.extra.answer[responseTranslation || translation][keyName]
                  : response.answer[keyName]

              const pipingMask =
                '*' != (responseTranslation || translation) &&
                Object.hasOwn(response, 'extra') &&
                Object.hasOwn(response.extra, 'pipingMask') &&
                Object.hasOwn(response.extra.pipingMask, responseTranslation || translation) &&
                Object.hasOwn(response.extra.pipingMask[responseTranslation || translation], `___${path}t`)
                  ? Object.keys(response.extra.pipingMask[responseTranslation || translation]).reduce((acc, cur) => {
                      acc[cur] =
                        'string' == typeof response.extra.pipingMask[responseTranslation || translation][cur]
                          ? response.extra.pipingMask[responseTranslation || translation][cur]
                          : Object.hasOwn(response.extra.pipingMask[responseTranslation || translation][cur], 't')
                          ? response.extra.pipingMask[responseTranslation || translation][cur].t
                          : null
                      return acc
                    }, {})
                  : response.pipingMask

              return (
                <OEItem
                  key={`Response${responseId}_Question${keyName}`}
                  dataId={`Response${responseId}_Question${keyName}`}
                  responseId={responseId}
                  path={path}
                  answer={answer}
                  survey={survey}
                  question={question}
                  tags={responseTagsByPath(response, pathQuestionIdToOld(path))}
                  selectedQuestion={selectedQuestion}
                  pipingMask={Object.assign({}, response.pipingMask, pipingMask)}
                  toolBoxOptions={toolBoxOptions}
                  isRejected={isRejected}
                  voiceBusy={voiceBusy}
                  setVoiceBusy={setVoiceBusy}
                  onUpdateSchema={onUpdateSchema}
                  onUpdateParams={onUpdateParams}
                />
              )
            })}
          </div>

          <svg
            viewBox="0 0 100 100"
            className={classNames(style['response-dialog-triangle'], {
              [style[`sentiment-${lastSentimentValue ? lastSentimentValue : 'None'}`]]:
                isShowSentiments && sentimentStatus && false !== lastSentimentValue
            })}
          >
            <polygon points="0,0 100,0 0,100" />
          </svg>
        </div>
      ) : null
    }
  )
)

Response.propTypes = {
  dataId: PropTypes.string,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  survey: PropTypes.object,
  reportVirtual: PropTypes.object,
  reportSchema: PropTypes.array,
  response: PropTypes.object,
  toolBoxOptions: PropTypes.array,
  showTraitsRef: PropTypes.object,
  responseOrderRef: PropTypes.object,
  isVirtualOrXpertInScope: PropTypes.bool,
  isOnlyVirtualOrXpert: PropTypes.bool,
  popupNotificationsDisabled: PropTypes.object,
  isShowHiddenFilters: PropTypes.bool,
  onUpdateSchema: PropTypes.func,
  onUpdateParams: PropTypes.func,
  onCloseNotification: PropTypes.func
}

Response.displayName = 'Response'

export default Response
